import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PopupComponent } from '../popup/popup.component';
import { TranslationsManagementService } from 'src/app/services/translations-management.service';
import { EncryptionService } from 'src/app/services/encryption.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, TablerIconsModule, TranslateModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {

  passwordVisible: boolean = false;
  pwdInputType: string = "password";
  currentYear : Number = new Date().getFullYear();

  login = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  public selectedLanguage: any = {
    language: 'English',
    code: 'en',
    type: 'EN',
    icon: '/assets/images/flag/icon-flag-en.png',
  };

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'EN',
      icon: '/assets/images/flag/icon-flag-en.png',
    },
    {
      language: 'Italiano / Italian',
      code: 'it',
      type: 'IT',
      icon: '/assets/images/flag/icon-flag-it.png',
    },
    {
      language: 'Nederlands / Dutch',
      code: 'nl',
      icon: '/assets/images/flag/icon-flag-nl.png',
    },
    {
      language: 'Euskara / Basque',
      code: 'eu',
      icon: '/assets/images/flag/icon-flag-eu.png',
    },
    {
      language: 'Català-Valencià / Catalan-Valencian',
      code: 'ca',
      icon: '/assets/images/flag/icon-flag-ca.png',
    },
    {
      language: 'Español / Spanish',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg',
    },
    {
      language: 'Slovenščina / Slovenian',
      code: 'sl',
      icon: '/assets/images/flag/icon-flag-sl.png',
    },
    {
      language: 'Ελληνικά / Greek',
      code: 'el',
      icon: '/assets/images/flag/icon-flag-el.png',
    },
    {
      language: 'Română / Romanian',
      code: 'ro',
      icon: '/assets/images/flag/icon-flag-ro.png',
    },
    {
      language: 'Deutsch / German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.png',
    }
  ];


  private authConfirmedSubscription: Subscription = new Subscription();
  private profileSubscription: Subscription = new Subscription();
  private logsSubscription: Subscription = new Subscription();

  constructor(public dialog: MatDialog, private router: Router, private translateService: TranslateService, private authService: AuthService,
    private loadingService: LoadingService, private translationsManagementService: TranslationsManagementService, private encryptionService: EncryptionService) {

  }

  ngOnInit() {

    const language = localStorage.getItem('language');
    const found = this.languages.find((item: any) => item.code === language);

    this.selectedLanguage = found ? found : {
      language: 'English',
      code: 'en',
      type: 'EN',
      icon: '/assets/images/flag/icon-flag-en.png',
    };

    this.authConfirmedSubscription = this.authService.authConfirmed$.subscribe((data: any) => {

      if (data.indexOf('error') != -1) {

        this.loadingService.changeSpinnerStatus("false", "login");

        // When the user is different from Educator and Pilot admin
        if (data == 'errorRole') {
          this.showErrorPopUp("login.loginErrorRole");
        }

        //Error Popup 5XX
        else if (data.indexOf("error5") != -1) {
          this.showErrorPopUp("general.errorMessage");
          //Error Popup 401 Account not found
        } else if (data.indexOf("error401") != -1 && data.indexOf("account_not_found") != -1) {
          this.showErrorPopUp("login.loginErrorAccount");
        } 
        //Error Popup 401 Incorrect password
        else if (data.indexOf("error401") != -1 && data.indexOf("incorrect_password") != -1) {
          this.showErrorPopUp("login.loginErrorPassword");
        }
        //Error Popup 401 Registration incomplete 
        else if (data.indexOf("error401") != -1 && data.indexOf("registration_incomplete") != -1) {
          this.showErrorPopUp("login.loginErrorRegistration");
        } else {
          this.showErrorPopUp("general.errorMessage");
        }

      } else {

        //If the email and pwd are valid, the user is stored in localStorage for automatic login
        if (this.login.controls.email.value) {
          sessionStorage.setItem("email", this.encryptionService.encrypt(this.login.controls.email.value));
        }

        if (this.login.controls.password.value) {
          sessionStorage.setItem("pwd", this.encryptionService.encrypt(this.login.controls.password.value));
        }


        this.authService.getUserProfile(this.authService.userSessionInfo.user_uuid);
      }

    });

    this.profileSubscription = this.authService.profileData$.subscribe((data: any) => {

      if (data.indexOf('error') != -1) {

        //Error Popup 401
        if (data.indexOf("error401") != -1) {
          this.showErrorPopUp("general.errorMessageSessionExpired");
        } else if(data.indexOf("errorProfile") != -1){

           this.showErrorPopUp("login.loginError");

        } else {
          this.showErrorPopUp("general.errorMessage");
        }

        this.loadingService.changeSpinnerStatus("false", "login");

      } else {

        this.authService.saveLogs();

      }

    });

    this.logsSubscription = this.authService.logsSaved$.subscribe((data: any) => {

      if (data.indexOf('error') != -1) {

        //Error Popup 401
        if (data.indexOf("error401") != -1) {
          this.showErrorPopUp("general.errorMessageSessionExpired");
        } else {
          this.showErrorPopUp("general.errorMessage");
        }

        this.loadingService.changeSpinnerStatus("false", "login");

      } else {
        if(this.authService.role == "ADMIN_ROLE" || this.authService.role == "TEACHER_ROLE"){
            this.router.navigate(['/section/home']);
        }else{
          this.router.navigate(['/section/analysis']);
        }
      }

    });

    //Automatic login, if the user is saved in sessionStorage, the login is automatic
    if (sessionStorage.getItem("email") && sessionStorage.getItem("pwd")) {

      let email = sessionStorage.getItem("email") ?? '';
      let pwd = sessionStorage.getItem("pwd") ?? '';

      if (email != '' && pwd != '') {
        this.login.controls.email.setValue(this.encryptionService.decrypt(email));
        this.login.controls.password.setValue(this.encryptionService.decrypt(pwd));
      }

      this.submitLogin();

    }

  }

  get f() {
    return this.login.controls;
  }

  /**
   * Method to check credentials and login
   */
  submitLogin() {

    this.loadingService.changeSpinnerStatus("true", "login");

    if (this.login.controls.email.value && this.login.controls.password.value) {
      this.authService.login(this.login.controls.email.value, this.login.controls.password.value)
    }
  }

  /**
   * Method to change password visibility
   */
  changePasswordVisibility() {
    if (this.pwdInputType == 'password') {
      this.pwdInputType = 'text';
      this.passwordVisible = true;
    } else {
      this.pwdInputType = 'password';
      this.passwordVisible = false;
    }
  }

  /**
   * Method to change the language
   * @param lang selected language
   */
  changeLanguage(lang: any): void {
    this.translationsManagementService.changeLanguage(lang.code);
    this.selectedLanguage = lang;
  }

  recoverPassword() {
    this.dialog.open(PopupComponent, {
      disableClose: true,
      width: '640px',
      height: '225px',
      data: {
        type: "recoverPassword",
        headerText: this.translateService.instant("login.recoverPassword"),
        generalText: this.translateService.instant("login.recoverPasswordInfo"),
        button: ["Ok"]
      },
    }).afterClosed().subscribe(async (result) => {

      if (result && result.data) {
        this.loadingService.changeSpinnerStatus("true", "login");

        await this.authService.recoverPassword(result.data).subscribe((data: any) => {

          this.loadingService.changeSpinnerStatus("false", "login");
          this.showErrorPopUp("login.emailSent");

        }, error => {
          console.log("Error in recoverPassword:", error);

          if(error.error.status == 400) {
            this.showErrorPopUp("login.emailNotFound");
          }else {
            this.showErrorPopUp("general.errorMessage");
          }
          this.loadingService.changeSpinnerStatus("false", "login");
          

        });
      }

    });
  }

  /**
 * Pop-up to show an error ocurred
 */
  showErrorPopUp(message: string) {

    let generalText;
    let headerText;

    if(message == 'login.loginError' || "login.loginErrorAccount" || "login.loginErrorPassword" || "login.loginErrorRegistration"){
        generalText = this.translateService.instant(message) +" "+ `<a href="mailto:sunrise_support@iti.gr?subject=SUNRISE Authoring tool">${this.translateService.instant('login.contactSupport')}</a>.`;
    }else{
      generalText = this.translateService.instant(message);
    }

    if(message == "login.loginErrorAccount") {

      headerText = this.translateService.instant("login.attention") + ": "+ this.translateService.instant("login.loginErrorAccountHeader")

    }else if (message == "login.loginErrorPassword"){

      headerText = this.translateService.instant("login.attention") + ": "+ this.translateService.instant("login.loginErrorPasswordHeader")

    }else if (message == "login.loginErrorRegistration"){

      headerText = this.translateService.instant("login.attention") + ": "+ this.translateService.instant("login.loginErrorRegistrationHeader")

    }else{

      headerText = this.translateService.instant("login.attention")

    }

    this.loadingService.changeSpinnerStatus("false", "educators-table");
    //SHOW ERROR POPUP
    this.dialog.open(PopupComponent, {
      disableClose: true,
      width: '640px',
      height: 'auto',
      data: {
        type: "attentionInfo2",
        headerText: headerText,
        generalText: generalText,
        button: ["Ok"]
      },
    }).afterClosed().subscribe(async (result) => {

      if (message == 'general.errorMessageSessionExpired') {
        this.router.navigate(['/']);
      }

    })

  }

    navigatePrivacyPolicy() {
    this.router.navigate(['/privacyPolicy']);
  }

  /**
   * Executed when leaving the page
   */
  ngOnDestroy() {
    this.authConfirmedSubscription.unsubscribe();
    this.profileSubscription.unsubscribe();
    this.logsSubscription.unsubscribe();
  }

}
